import React, { useState, useEffect, useRef } from 'react';
import { uploadImageToCloudinaryWithProgress } from '../../../../api/external/cloudinary/upload';

import EditModalShell from '../forms/EditModalShell/EditModalShell';
import LoadingModal from '../LoadingModal/LoadingModal';
import AdminProgressBar from '../../AdminProgressBar/AdminProgressBar';
import ImageInputWithPreview from '../forms/ImageInputWithPreview/ImageInputWithPreview';
import FormField from '../forms/FormField/FormField';

import './TeamMemberModal.css';

const TeamMemberModal = ({ onClose, onSubmit, member, mode }) => {
  const initialFormState = {
    firstName: '',
    lastName: '',
    jobTitle: '',
    description: '',
    imageUrl: '', // image path
    imagePublicId: '',
    janeUrl: '',
    credUrl: '',
    order: 0,
  };

  const [formData, setFormData] = useState(initialFormState);
  const [selectedFile, setSelectedFile] = useState(null); // raw file
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);

  const {
    firstName,
    lastName,
    jobTitle,
    description,
    imageUrl,
    janeUrl,
    credUrl,
    order,
  } = formData;

  const firstInputRef = useRef();

  useEffect(() => {
    setFormData({
      firstName: member?.firstName || '',
      lastName: member?.lastName || '',
      jobTitle: member?.jobTitle || '',
      description: member?.description || '',
      imageUrl: member?.imageUrl || '',
      imagePublicId: member?.imagePublicId || '',
      janeUrl: member?.janeUrl || '',
      credUrl: member?.credUrl || '',
      order: member?.order || 0,
    });

    setSelectedFile(null);
    setImagePreviewUrl(member?.imageUrl || '');

    firstInputRef.current?.focus();
  }, [mode, member]);

  useEffect(() => {
    return () => {
      if (imagePreviewUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreviewUrl);
      }
    };
  }, [imagePreviewUrl]);

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'number' ? Number(value) : value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setSelectedFile(file);

    // revoke old blob if exists
    if (imagePreviewUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreviewUrl);
    }

    setImagePreviewUrl(URL.createObjectURL(file));
  };

  const handleRemoveImage = () => {
    if (imagePreviewUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreviewUrl);
    }

    setSelectedFile(null);
    setImagePreviewUrl(null);

    setFormData((prev) => ({
      ...prev,
      imageUrl: '',
      imagePublicId: '',
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);
    setProgress(0);

    try {
      let uploadedData = null;

      if (selectedFile) {
        uploadedData = await uploadImageToCloudinaryWithProgress(
          selectedFile,
          setProgress,
          'team',
          [0, 70],
        );
      }

      const finalData = {
        ...formData,
        imageUrl: uploadedData?.url || formData.imageUrl || '',
        imagePublicId: uploadedData?.publicId || formData.imagePublicId || '',
      };

      setProgress(100);
      await onSubmit(finalData);

      onClose();
    } catch (err) {
      console.error('Submit failed:', err);
      alert('Something went wrong.  Please try again.');
    } finally {
      setLoading(false);
      setProgress(0);
    }
  };

  return (
    <EditModalShell
      formId='team-member-modal-form'
      onClose={onClose}
      onSubmit={handleSubmit}
      mode={mode}
      isLoading={loading}
      itemLabel='Team Member'
    >
      {loading && (
        <LoadingModal>
          <AdminProgressBar progress={progress} />
        </LoadingModal>
      )}

      <ImageInputWithPreview
        imagePreviewUrl={imagePreviewUrl}
        imageUrl={imageUrl}
        currentImgAlt='Current Team Member'
        onFileChange={handleFileChange}
        onRemoveImage={handleRemoveImage}
      />

      <FormField
        type='number'
        name='order'
        label='Order'
        value={order}
        onChange={handleInputChange}
        required
      />

      <FormField
        type='text'
        name='firstName'
        label='First Name'
        value={firstName}
        onChange={handleInputChange}
        ref={firstInputRef}
        required
      />

      <FormField
        type='text'
        name='lastName'
        label='Last Name'
        value={lastName}
        onChange={handleInputChange}
        required
      />

      <FormField
        type='text'
        name='jobTitle'
        label='Job Title'
        value={jobTitle}
        onChange={handleInputChange}
        required
      />

      <FormField
        type='textarea'
        name='description'
        label='Description'
        value={description}
        onChange={handleInputChange}
      />

      <FormField
        type='url'
        name='janeUrl'
        label='Booking URL'
        value={janeUrl}
        onChange={handleInputChange}
        autoComplete='on'
        required
      />
      <FormField
        type='url'
        name='credUrl'
        label='Cred URL'
        value={credUrl}
        onChange={handleInputChange}
        autoComplete='on'
        required
      />
    </EditModalShell>
  );
};

export default TeamMemberModal;
